import React, {useRef} from 'react';
import {useToast} from 'react-native-fast-toast';

import ShareDoctorProfilePanel from './ShareDoctorProfilePanel';

interface ShareDoctorProfileModalProps {
  shareUrl: string;
  onClose?: () => void;
}

/**
 * Web container for the Share panel. Deliberately uses only browser APIs
 * (navigator.clipboard, navigator.share, a synthetic download link) - see
 * ShareDoctorProfilePanel.tsx for why native-only packages can't be
 * imported here even behind a Platform.OS check.
 */
const ShareDoctorProfileModal = ({shareUrl, onClose}: ShareDoctorProfileModalProps) => {
  const toast = useToast();
  const qrSvgRef = useRef<any>(null);

  const getQrBase64 = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!qrSvgRef.current) {
        reject(new Error('QR code is not ready yet'));
        return;
      }
      qrSvgRef.current.toDataURL((base64: string) => resolve(base64));
    });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast?.show('Link copied to clipboard', {type: 'success'});
    } catch (err) {
      console.log('copy link error', err);
      toast?.show('Unable to copy the link. Please try again.', {type: 'danger'});
    }
  };

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({url: shareUrl});
      } else {
        await copyLink();
      }
    } catch (err) {
      // User cancelling the share sheet also rejects this promise - not an error.
      console.log('share link cancelled or failed', err);
    }
  };

  const downloadQr = async () => {
    try {
      const base64 = await getQrBase64();
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${base64}`;
      link.download = 'doctor-profile-qr.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast?.show('QR code saved', {type: 'success'});
    } catch (err) {
      console.log('download qr error', err);
      toast?.show('Unable to save the QR code. Please try again.', {type: 'danger'});
    }
  };

  const shareQr = async () => {
    try {
      const base64 = await getQrBase64();
      const dataUrl = `data:image/png;base64,${base64}`;

      if (navigator.share) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'doctor-profile-qr.png', {type: 'image/png'});
        if (!navigator.canShare || navigator.canShare({files: [file]})) {
          await navigator.share({files: [file], title: 'Doctor Profile QR Code'});
          return;
        }
      }
      // No Web Share API (or file-sharing) support - fall back to a download.
      await downloadQr();
    } catch (err) {
      console.log('share qr cancelled or failed', err);
    }
  };

  return (
    <ShareDoctorProfilePanel
      shareUrl={shareUrl}
      onClose={onClose}
      onCopyLink={copyLink}
      onShareLink={shareLink}
      onDownloadQr={downloadQr}
      onShareQr={shareQr}
      getQrRef={ref => {
        qrSvgRef.current = ref;
      }}
    />
  );
};

export default ShareDoctorProfileModal;
